import React, { useEffect, useMemo } from 'react'

import { clearSubstitutedArticles } from '../../store/articleSubstitutions/articleSubstitutionsSlice'
import { fetchSubstitutedArticleTitles } from '../../store/requests/thunks'
import { getArticleLabel } from '../../helpers/getArticleLabel'
import { setIsArticleSubstitutionsModalOpen } from '../../store/ui/uiSlice'
import Modal from '../Modal/Modal'
import Heading from '../Heading/Heading'

import * as commonStyles from '../styles'
import * as styles from './styles'
import { useAppDispatch, useAppSelector } from '../../hooks'

const Styled = {
  ...commonStyles,
  ...styles,
}

const ArticleSubstitutionsModal: React.FC = () => {
  const { isArticleSubstitutionsModalOpen: isOpen } = useAppSelector((state) => state.ui) || {}
  const { content } = useAppSelector((state) => state.init) || {}
  const { substitutedArticles, substitutedArticleTitles, unresolvedErpItemNumbers } = useAppSelector(
    (state) => state.articleSubstitutions
  )
  const dispatch = useAppDispatch()

  const erpItemNumbersWithoutTitle = useMemo(
    () => [
      ...new Set(
        substitutedArticles.flatMap(({ oldErpItemNumber, newErpItemNumber }) =>
          [oldErpItemNumber, newErpItemNumber].flatMap((erpItemNumber) =>
            erpItemNumber && !substitutedArticleTitles[erpItemNumber] ? [erpItemNumber] : []
          )
        )
      ),
    ],
    [substitutedArticles, substitutedArticleTitles]
  )

  useEffect(() => {
    if (!isOpen || erpItemNumbersWithoutTitle.length === 0) return

    dispatch(fetchSubstitutedArticleTitles({ erpItemNumbers: erpItemNumbersWithoutTitle }))
  }, [isOpen, erpItemNumbersWithoutTitle])

  const closeModal = (): void => {
    dispatch(setIsArticleSubstitutionsModalOpen(false))
    dispatch(clearSubstitutedArticles())
  }

  const articleLabel = (erpItemNumber: string): string =>
    getArticleLabel(substitutedArticleTitles[erpItemNumber], erpItemNumber, content?.other?.artNo)

  // A null newErpItemNumber from DM means the article was removed without a replacement, which is a resolved outcome.
  // Only the ones we synthesised after DM's error event are unresolved, and those are exactly unresolvedErpItemNumbers.
  const isUnresolved = (erpItemNumber: string | null): boolean =>
    erpItemNumber !== null && unresolvedErpItemNumbers.includes(erpItemNumber)

  // Also true for an empty list, which the reducer can leave behind: the alternatives below would promise rows that aren't there
  const showUnresolvedInformation =
    substitutedArticles.length === 0 ||
    substitutedArticles.some(({ oldErpItemNumber }) => isUnresolved(oldErpItemNumber))
  const isOnlyOutdated = substitutedArticles.every(({ reason }) => reason === 'outdated')

  const informationText = showUnresolvedInformation
    ? content?.modals?.articleSubstitutions?.informationUnresolved
    : isOnlyOutdated
      ? content?.modals?.articleSubstitutions?.informationOutdated
      : content?.modals?.articleSubstitutions?.informationMissing

  return (
    <Modal isOpen={isOpen} onRequestClose={closeModal} contentLabel={content?.modals?.articleSubstitutions?.title}>
      <Styled.ModalBackPrompt>
        <Heading level={2}>{content?.modals?.articleSubstitutions?.title}</Heading>
        <p>{informationText}</p>
        <Styled.ArticleSubstitutionsList>
          {substitutedArticles.map(({ oldErpItemNumber, newErpItemNumber }) => (
            <li key={`${oldErpItemNumber}--${newErpItemNumber}`}>
              <span>{oldErpItemNumber && articleLabel(oldErpItemNumber)}</span>
              <span>
                {newErpItemNumber
                  ? articleLabel(newErpItemNumber)
                  : isUnresolved(oldErpItemNumber)
                    ? content?.modals?.articleSubstitutions?.notSubstituted
                    : content?.modals?.articleSubstitutions?.removedWithoutReplacement}
              </span>
            </li>
          ))}
        </Styled.ArticleSubstitutionsList>
        <Styled.Button className="button-primary" onClick={closeModal}>
          {content?.modals?.articleSubstitutions?.ok}
        </Styled.Button>
      </Styled.ModalBackPrompt>
    </Modal>
  )
}

export default ArticleSubstitutionsModal
